// Assignment writers (F-ASGN-*). Ports the v_20 ownership model EXACTLY:
// assignCase / pickUpCase / bulkAssign, plus the pure guards that decide which
// button shows, whether a note is required, and how a claim-safe / stale-guard
// write reports a miss. (Legacy assignCase/pickUpCase/execAssignSingle/
// execBulkAssign/openAssignModal ruling, v20 lines ~4606-4774, 5490-5500.)
//
// v_20 ownership rules preserved verbatim:
//  - ANY staff member may pick up an UNASSIGNED patient (continuity when a
//    coordinator is away) — claim-safe: the write only lands while still
//    unowned, and the race loser is TOLD, never silently overwritten.
//  - Reassigning an OWNED case is open to all staff (one "Reassign" verb, no
//    rights gate) but is expected-owner guarded (STALE if ownership changed
//    under the modal) and REQUIRES a timeline note when moving someone else's
//    patient.
//  - Bulk "Assign to…" stays managerial (canAssign only).
//  - Promote-time auto-assign fills the new owner claim-safe (TAKEN is
//    informational — a dedupe match may already have an owner).
//
// Every write returns the {ok,data,error,code} envelope. A guard MISS is a
// non-error outcome with a code (TAKEN / STALE) and no driver error, so callers
// branch on code and only route real errors through netErr.

import { getClient, ok, fail } from '../db/client.js';
import { TABLES, RPCS } from '../db/contract.js';
import { store, SLICES } from '../state/store.js';
import { logAudit, AUDIT_EVENT } from './audit.js';
import { userName, assignableUsers as _assignableUsers, activeUsers as _activeUsers } from './users.js';

// Re-export the user selectors the assignment UI reaches for, so callers can
// import "who can I assign to" from the assignment module (Task A surface)
// while the pure logic stays in users.js.
export const assignableUsers = _assignableUsers;
export const activeUsers = _activeUsers;

// ─── Pure guards (unit-tested without a DB) ────────────────────────────────

/**
 * Classify a write outcome from the guard opts and the rows the conditional
 * update returned. An empty result under onlyIfUnassigned means a colleague
 * claimed it first (TAKEN); under expectedOwner it means ownership already
 * moved (STALE). No guard, or a non-empty result, is a clean write (null).
 * Exact legacy branch.
 * @param {{onlyIfUnassigned?: boolean, expectedOwner?: string, fromUser?: any}} opts
 * @param {any[]|null|undefined} rows the .select() result of the guarded update
 * @returns {'TAKEN'|'STALE'|null}
 */
export function assignResultFor(opts, rows) {
  const empty = !rows || !rows.length;
  if (opts && opts.onlyIfUnassigned && empty) return 'TAKEN';
  if (opts && opts.expectedOwner && empty) return 'STALE';
  return null;
}

/**
 * Was this a SELF pick-up? (self-target + claim-safe + not at promote-time).
 * @param {{onlyIfUnassigned?: boolean, at_promote?: boolean}} opts
 * @param {any} uid target owner
 * @param {any} selfId signed-in user id
 * @returns {boolean}
 */
export function isSelfPickup(opts, uid, selfId) {
  return uid === selfId && !!(opts && opts.onlyIfUnassigned) && !(opts && opts.at_promote);
}

/**
 * The audit "kind" for a move (legacy kind computation). Depends on the
 * RESOLVED fromUser, so pass it in.
 * @param {{kind?: string, at_promote?: boolean, onlyIfUnassigned?: boolean, expectedOwner?: string}} opts
 * @param {{uid: any, selfId: any, fromUser: any}} ctx
 * @returns {'promote'|'pickup'|'reassign'|'assign'}
 */
export function assignKind(opts, ctx) {
  if (opts && opts.kind) return /** @type {'promote'|'pickup'|'reassign'|'assign'} */ (opts.kind);
  if (opts && opts.at_promote) return 'promote';
  if (isSelfPickup(opts, ctx.uid, ctx.selfId)) return 'pickup';
  if ((opts && opts.expectedOwner) || ctx.fromUser) return 'reassign';
  return 'assign';
}

/**
 * The audit summary sentence (legacy summary branch). Pure — names passed in.
 * @param {'promote'|'pickup'|'reassign'|'assign'} kind
 * @param {{uid: any, selfId: any, toName: string, fromName: string|null, selfPick: boolean}} ctx
 * @returns {string}
 */
export function assignSummary(kind, ctx) {
  if (kind === 'promote') return 'assigned new patient to ' + (ctx.uid === ctx.selfId ? 'self' : ctx.toName) + ' at intake';
  if (ctx.selfPick) return 'picked up unassigned patient (self-assign)';
  if (kind === 'reassign') return 'reassigned patient to ' + ctx.toName + (ctx.fromName ? ' (from ' + ctx.fromName + ')' : '');
  return 'assigned patient to ' + ctx.toName;
}

/**
 * Note-required rule: a note is mandatory only when MOVING a case that someone
 * ELSE owns (legacy execAssignSingle guard). Assigning an unowned case, or
 * re-routing your own, needs no note.
 * @param {string|null|undefined} curOwner the case's current owner
 * @param {string|null|undefined} selfId
 * @returns {boolean}
 */
export function noteRequired(curOwner, selfId) {
  return !!curOwner && curOwner !== selfId;
}

/**
 * Which quick-assign buttons a Today/registry row shows (legacy renderInbox
 * quickAssign). Only in the Legacy-pool ('unassigned') scope, only on real
 * cases (consent items have no case yet; safety items bypass scope): a
 * coordinator/admin gets one "Assign ▾", plain staff get "Pick up" + "Assign ▾".
 * @param {{scopeMode: string, isConsent: boolean, isSafety: boolean, canAssign: boolean}} ctx
 * @returns {Array<'pickup'|'assign'>}
 */
export function quickAssignButtons(ctx) {
  if (ctx.scopeMode !== 'unassigned' || ctx.isConsent || ctx.isSafety) return [];
  return ctx.canAssign ? ['assign'] : ['pickup', 'assign'];
}

// ─── Writers (envelope + audit) ────────────────────────────────────────────

function selfIdOf(deps) {
  if (deps && 'selfId' in deps) return deps.selfId;
  const s = store.get(SLICES.SESSION) || {};
  return s.user && s.user.id;
}

/**
 * The shared assignment writer (single + bulk + pick-up + reassign + promote).
 * Updates cases.assigned_to, optionally appends a clinical note atomically via
 * rpc_append_note (a note FAILURE never rolls back the saved move — it is
 * flagged in data.noteFailed for the caller to fold into one honest message),
 * then audits case_assigned through the retry-queued logAudit.
 *
 * Returns the envelope:
 *  - ok({caseId, kind, toUser, fromUser, noteFailed}) on a landed write;
 *  - fail(null, 'TAKEN') when a claim-safe pick-up lost the race;
 *  - fail(null, 'STALE') when an expected-owner reassign found ownership moved;
 *  - fail(error) on a real driver error (caller routes through netErr).
 *
 * @param {string} caseId
 * @param {string} uid new owner
 * @param {{onlyIfUnassigned?: boolean, expectedOwner?: string, note?: string, kind?: string, at_promote?: boolean, fromUser?: any}} [opts]
 * @param {{client?: any, selfId?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function assignCase(caseId, uid, opts, deps) {
  opts = opts || {};
  const client = (deps && deps.client) || getClient();
  const selfId = selfIdOf(deps);

  // Resolve fromUser for the audit A→B history. The guard paths already know
  // it; a plain unconditional assign captures the previous owner with a light
  // read BEFORE the update so history reads correctly.
  let fromUser = opts.fromUser;
  if (fromUser === undefined) {
    if (opts.onlyIfUnassigned) fromUser = null;
    else if (opts.expectedOwner) fromUser = opts.expectedOwner;
    else {
      try {
        const { data: pc } = await client.from(TABLES.CASES).select('assigned_to').eq('case_id', caseId).maybeSingle();
        fromUser = pc ? pc.assigned_to : null;
      } catch {
        fromUser = null;
      }
    }
  }

  let q = client.from(TABLES.CASES).update({ assigned_to: uid, updated_at: new Date().toISOString() }).eq('case_id', caseId);
  if (opts.onlyIfUnassigned) q = q.is('assigned_to', null); // claim-safe: only lands if still unowned
  if (opts.expectedOwner) q = q.eq('assigned_to', opts.expectedOwner); // stale-guard: only lands if unchanged
  const { data, error } = await q.select('case_id');
  if (error) return fail(error);

  const miss = assignResultFor(opts, data);
  if (miss) return fail(null, miss);

  const selfPick = isSelfPickup(opts, uid, selfId);
  const kind = assignKind(opts, { uid, selfId, fromUser });

  // Optional clinical note — appended atomically server-side (a read-modify-write
  // on stale notes would clobber concurrent edits). A note failure does NOT roll
  // back the (already saved) reassignment; flag it for a single combined message.
  let noteFailed = false;
  if (opts.note) {
    const { error: noteErr } = await client.rpc(RPCS.APPEND_NOTE, {
      p_case_id: caseId,
      p_note: '--- REASSIGN ---\n' + opts.note,
      p_mode: 'append',
    });
    if (noteErr) {
      noteFailed = true;
      console.warn('rpc_append_note failed', noteErr);
    }
  }

  const summary = assignSummary(kind, {
    uid,
    selfId,
    toName: userName(uid),
    fromName: fromUser ? userName(fromUser) : null,
    selfPick,
  });
  /** @type {Record<string, any>} */
  const details = { to_user: uid, from_user: fromUser || null, self_pickup: !!selfPick, kind };
  if (opts.at_promote) details.at_promote = true;
  if (opts.note) details.notes = opts.note;
  await logAudit(AUDIT_EVENT.CASE_ASSIGNED, summary, caseId, details, deps); // retry-queued on failure

  return ok({ caseId, kind, toUser: uid, fromUser: fromUser || null, noteFailed });
}

/**
 * Self-claim an unassigned patient (legacy pickUpCase). Claim-safe: TAKEN when
 * a colleague won the race. Convenience wrapper over assignCase.
 * @param {string} caseId
 * @param {{client?: any, selfId?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function pickUpCase(caseId, deps) {
  const selfId = selfIdOf(deps);
  if (!selfId) return fail(new Error('User not identified'), 'NO_USER');
  return assignCase(caseId, selfId, { onlyIfUnassigned: true }, deps);
}

/**
 * Reassign/route an owned-or-unowned case through the modal path (legacy
 * execAssignSingle's write half). curOwner drives the guard: an owned case is
 * expected-owner guarded (STALE-safe) and requires a note when moving someone
 * else's; an unowned case is claim-safe (TAKEN-safe). The note-required rule is
 * enforced here too (defence in depth — the UI also checks it) and returns
 * fail(..., 'NOTE_REQUIRED') so the modal stays open.
 * @param {string} caseId
 * @param {string} uid new owner
 * @param {{curOwner?: string|null, note?: string}} ctx modal context
 * @param {{client?: any, selfId?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function reassignCase(caseId, uid, ctx, deps) {
  const selfId = selfIdOf(deps);
  const curOwner = (ctx && ctx.curOwner) || null;
  const note = (ctx && ctx.note && String(ctx.note).trim()) || '';
  if (noteRequired(curOwner, selfId) && !note) return fail(null, 'NOTE_REQUIRED');
  const opts = curOwner
    ? { expectedOwner: curOwner, fromUser: curOwner, kind: 'reassign', note: note || undefined }
    : { onlyIfUnassigned: true, note: note || undefined };
  return assignCase(caseId, uid, opts, deps);
}

/**
 * Bulk "Assign to…" over selected registry rows (legacy execBulkAssign, canAssign
 * gate). Skips rows the target already owns (a no-op self-reassign would inflate
 * the ownership timeline). Each row's previous owner comes from the passed
 * `cases` cache when present (cheap A→B history) else assignCase's light read.
 * Runs concurrently; a partial failure surfaces the count and keeps the rest.
 * @param {string[]} caseIds
 * @param {string} uid target owner
 * @param {{cases?: any[]}} [opts] the loaded registry cache for from_user + skip
 * @param {{client?: any, selfId?: any, session?: any, role?: string}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function bulkAssign(caseIds, uid, opts, deps) {
  const cases = (opts && opts.cases) || [];
  const byId = new Map(cases.map((c) => [c.case_id, c]));
  const skipped = caseIds.filter((id) => { const p = byId.get(id); return p && p.assigned_to === uid; });
  const todo = caseIds.filter((id) => { const p = byId.get(id); return !p || p.assigned_to !== uid; });
  if (!todo.length) return ok({ assigned: 0, skipped: skipped.length, failed: 0, done: true });

  const results = await Promise.allSettled(
    todo.map((id) => {
      const prev = byId.get(id);
      return assignCase(id, uid, { fromUser: prev ? prev.assigned_to || null : undefined }, deps).then((r) => {
        if (!r.ok) throw r.error || new Error(r.code || 'assign failed');
        return r;
      });
    })
  );
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length) {
    return Object.assign(fail(/** @type {any} */ (failed[0]).reason), {
      data: { assigned: todo.length - failed.length, skipped: skipped.length, failed: failed.length },
    });
  }
  return ok({ assigned: todo.length, skipped: skipped.length, failed: 0 });
}
